import apiClient from "../../api/api";

/**
 * Access level granted to a role.
 *
 * - `null`: No access.
 * - `"ownership"`: Access only to owned records.
 * - `"project-scoped"`: Access scoped to managed projects or teams.
 * - `"full"`: Full access.
 *
 * @typedef {"ownership" | "project-scoped" | "full" | null} PermissionGrant
 */

/**
 * Grants assigned to each application role.
 *
 * @typedef {Object<string, PermissionGrant>} RoleGrants
 */

/**
 * Role permission configuration for a specific endpoint and HTTP method.
 *
 * @typedef {Object} RolePermission
 * @property {string} endpoint - API endpoint associated with the permission.
 * @property {string} method - HTTP method, such as GET, POST, PUT, PATCH or DELETE.
 * @property {RoleGrants} grants - Access level assigned to each role.
 */

/**
 * Retrieves all configured role permissions.
 *
 * @async
 * @returns {Promise<Object>} API response containing the role permissions and pagination data.
 * @throws {Error} When the request cannot be completed.
 */
export const getRolePermissions = async () => {
    const response = await apiClient.get(
        `${import.meta.env.VITE_ROLE_PERMISSIONS_BASE_URL}?page=1&limit=1000`
    );

    return response.data;
};

/**
 * Updates the role permissions for an existing endpoint and HTTP method.
 *
 * @async
 * @param {RolePermission} rolePermission - Complete permission configuration to update.
 * @returns {Promise<Object>} Updated role permission returned by the API.
 * @throws {Error} When the update request cannot be completed.
 */
export const updateRolePermissions = async (rolePermission) => {
    const response = await apiClient.put(
        import.meta.env.VITE_ROLE_PERMISSIONS_BASE_URL,
        rolePermission,
        {
            successToast: "Role permission updated successfully",
        }
    );

    return response.data;
};

/**
 * Creates a role permission configuration for an endpoint and HTTP method.
 *
 * @async
 * @param {RolePermission} rolePermission - Permission configuration to create.
 * @returns {Promise<Object>} Created role permission returned by the API.
 * @throws {Error} When the creation request cannot be completed.
 */
export const createRolePermission = async (rolePermission) => {
    const response = await apiClient.post(
        import.meta.env.VITE_ROLE_PERMISSIONS_BASE_URL,
        rolePermission,
        {
            successToast: "Role permission created successfully",
        }
    );

    return response.data;
};

/**
 * Deletes the role permission associated with an endpoint and HTTP method.
 *
 * @async
 * @param {string} endpoint - API endpoint whose permission should be deleted.
 * @param {string} method - HTTP method associated with the endpoint.
 * @returns {Promise<Object>} Deletion response returned by the API.
 * @throws {Error} When the deletion request cannot be completed.
 */
export const deleteRolePermission = async (endpoint, method) => {
    const params = {
        endpoint,
        method,
    };

    const response = await apiClient.delete(
        import.meta.env.VITE_ROLE_PERMISSIONS_BASE_URL,
        {
            params,
            successToast: "Role permission deleted successfully",
        }
    );

    return response.data;
};